import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  query,
  orderBy,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';

const GENERATE_URL = 'https://us-central1-cndsoftware-70b14.cloudfunctions.net/generateProposal';

const PORTFOLIO_CACHE_TTL_MS = 5 * 60 * 1000;
let portfolioCache = { text: '', expiresAt: 0 };

const truncate = (str, max) => {
  const s = (str || '').replace(/\s+/g, ' ').trim();
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
};

const formatProject = (p) => {
  const parts = [`- ${p.title || 'Untitled'}`];
  if (p.type) parts.push(`(${p.type})`);
  if (p.tagline) parts.push(`— ${truncate(p.tagline, 120)}`);
  const meta = [];
  if (Array.isArray(p.technologies) && p.technologies.length) {
    meta.push(`tech: ${p.technologies.slice(0, 8).join(', ')}`);
  }
  if (p.description) meta.push(truncate(p.description, 240));
  const head = parts.join(' ');
  return meta.length ? `${head}\n    ${meta.join(' | ')}` : head;
};

const formatStudioItem = (i) => {
  const head = `- ${i.title || 'Untitled'}${i.type ? ` (${i.type})` : ''}`;
  const desc = i.description ? `\n    ${truncate(i.description, 240)}` : '';
  return `${head}${desc}`;
};

export const fetchPortfolioContext = async ({ force = false } = {}) => {
  const now = Date.now();
  if (!force && portfolioCache.text && portfolioCache.expiresAt > now) {
    return portfolioCache.text;
  }
  try {
    const [projectsSnap, studioSnap] = await Promise.all([
      getDocs(collection(db, 'projects')),
      getDocs(collection(db, 'studio_items')),
    ]);
    const projects = projectsSnap.docs.map((d) => d.data());
    const studioItems = studioSnap.docs.map((d) => d.data());

    const sections = [];
    if (projects.length) {
      sections.push(`Client projects (${projects.length}):\n${projects.map(formatProject).join('\n')}`);
    }
    if (studioItems.length) {
      sections.push(`Our products (${studioItems.length}):\n${studioItems.map(formatStudioItem).join('\n')}`);
    }
    const text = sections.length
      ? `CND Software portfolio reference (use to pick the single most relevant past project; do not list everything):\n\n${sections.join('\n\n')}`
      : '';

    portfolioCache = { text, expiresAt: now + PORTFOLIO_CACHE_TTL_MS };
    return text;
  } catch (err) {
    console.error('fetchPortfolioContext failed:', err);
    return portfolioCache.text || '';
  }
};

const postPrompt = async (promptInput) => {
  const res = await fetch(GENERATE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ promptInput }),
  });
  const raw = await res.text();
  let data = null;
  try { data = raw ? JSON.parse(raw) : null; } catch (_) { /* non-JSON body */ }
  return { ok: res.ok, status: res.status, data, raw };
};

const isRetryable = ({ status, data }) =>
  status >= 500 || (data && typeof data.error === 'string' && /empty response/i.test(data.error));

export const generateProposal = async ({ systemPrompt, jobDescription }) => {
  const portfolioContext = await fetchPortfolioContext();

  const promptInput = [
    systemPrompt.trim(),
    portfolioContext ? `---\n${portfolioContext}` : null,
    '---',
    `Upwork job description:\n${jobDescription.trim()}`,
    'Write the proposal now, following the rules above exactly. Pick the single most relevant past project from the portfolio reference above. Output only the proposal text.',
  ].filter(Boolean).join('\n\n');

  let result = await postPrompt(promptInput);

  if (!result.ok && isRetryable(result)) {
    await new Promise((r) => setTimeout(r, 800));
    result = await postPrompt(promptInput);
  }

  if (!result.ok) {
    const msg = result.data?.error || result.raw || 'Unknown error';
    if (/empty response/i.test(msg)) {
      throw new Error('The model returned an empty response. Try shortening the job description, or click Generate again.');
    }
    throw new Error(`Generation failed (${result.status}): ${msg}`);
  }

  if (!result.data?.text) {
    throw new Error('The model returned no text. Try again or shorten the job description.');
  }

  return result.data.text;
};

export const saveProposalDraft = async ({ templateId, templateName, jobDescription, generatedText }) => {
  const ref = await addDoc(collection(db, 'proposals'), {
    templateId: templateId || null,
    templateName: templateName || null,
    jobDescription: jobDescription || '',
    generatedText: generatedText || '',
    createdAt: serverTimestamp(),
  });
  return ref.id;
};

export const fetchRecentProposals = async (max = 20) => {
  const q = query(collection(db, 'proposals'), orderBy('createdAt', 'desc'), limit(max));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const deleteProposal = async (id) => {
  await deleteDoc(doc(db, 'proposals', id));
};
